import React from 'react';
import { HeadingText20, BodyTextSmall, pxToRem } from '@modernhealth/vital-components';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Conversation } from '@modernhealth/messaging-kit';
import { colors } from '@modernhealth/vital-foundation';

import { ProviderAssignmentDisplayType } from 'src/generated/webapp_gql';
import { Avatar } from 'src/modules/messaging/components/Avatar';
import { getConfigForKey } from 'src/shared/helpers/getConfigForKey';
import { useProvider } from 'src/modules/providers/hooks/useProvider';

import { ConversationHeaderActions } from './ConversationHeaderActions';
import { ConversationMessageCallout } from './ConversationMessageCallout';

export const ConversationHeaderTestId = 'ConversationSubRoute.ConversationHeaderTestId';

const HeaderContainer = styled.div({
    paddingRight: 0,
    paddingBottom: pxToRem(15),
    borderBottom: '2px solid ' + colors.gray05,
});

const Header = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
});

const HeaderAvatar = styled(Avatar)({
    marginRight: pxToRem(14),
});

const HorizontalGroup = styled.div({
    display: 'flex',
});

const VerticalGroup = styled.div({
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
});

interface Props {
    conversation: Conversation;
}

export const ConversationHeader: React.FC<Props> = ({ conversation }) => {
    const { t } = useTranslation();
    const { myProviderAssignments } = useProvider();

    const { coach } = conversation;
    const providerAssignment = myProviderAssignments.find(p => p.coach?.uuid === coach?.uuid);
    if (!providerAssignment?.coach) {
        return null;
    }

    const provider = providerAssignment.coach;
    const name = `${provider.firstName} ${provider.lastName}`;
    const providerTypeLabel = getConfigForKey<
        ProviderAssignmentDisplayType,
        string | undefined | null
    >(providerAssignment.providerDisplayType, {
        [ProviderAssignmentDisplayType.Psychiatrist]: provider.psychiatristSubtype,
        [ProviderAssignmentDisplayType.PathwaySpecialist]: t('pathways:pathway_specialist'),
        [ProviderAssignmentDisplayType.Coach]: t('providers:provider_type.coach'),
        [ProviderAssignmentDisplayType.Therapist]: t('providers:provider_type.therapist'),
    });

    return (
        <HeaderContainer>
            <Header data-testid={ConversationHeaderTestId}>
                <HorizontalGroup>
                    <HeaderAvatar diameter={40} user={coach} />
                    <VerticalGroup>
                        <HeadingText20 as="h2" marginBottom={0}>
                            {name}
                        </HeadingText20>
                        {!!providerTypeLabel && <BodyTextSmall>{providerTypeLabel}</BodyTextSmall>}
                    </VerticalGroup>
                </HorizontalGroup>
                <HorizontalGroup>
                    <ConversationHeaderActions coach={provider} />
                </HorizontalGroup>
            </Header>
            <ConversationMessageCallout coach={provider} />
        </HeaderContainer>
    );
};
